import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, ArrowRight, Loader2, AlertCircle, Info, Clock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errorMessage } from "@/lib/api";
import { usePlan, useDeliveryUpdater, useMeta } from "@/lib/queries";
import { weekLabel, formatDate, weekRange, PRODUCTION_STATUS_LABELS, INSTALL_STATUS_LABELS, SERVICE_STATUS_LABELS } from "@/lib/format";
import { PageHeader, LoadingBlock, ErrorBlock } from "@/components/common/Blocks";
import { splitProjectName, readableReason } from "@/lib/followup";
import { useElementWidth } from "@/lib/hooks";
import { SplitPage, PanelFrame, PanelSection } from "@/components/layout/SidePanel";

/* Activities are work (bars). Deliveries are milestones (markers). They must not look alike. */
const KINDS = {
  produksjon: { label: "Produksjon", color: "var(--kind-produksjon)", type: "bar" },
  montering: { label: "Montering", color: "var(--kind-montering)", type: "bar" },
  service: { label: "Service", color: "var(--kind-service)", type: "bar" },
  avtalt_levering: { label: "Avtalt levering", color: "var(--kind-levering)", type: "milestone" },
  onsket_levering: { label: "Ønsket levering", color: "var(--kind-onsket)", type: "milestone" },
};
const KIND_ORDER = ["produksjon", "montering", "service", "avtalt_levering", "onsket_levering"];
const STATE = {
  planlagt: { label: "Planlagt" },
  avventer: { label: "Avventer" },
  krever_oppfolging: { label: "Krever oppfølging" },
};
const WEEK_OPTIONS = [4, 8, 12];
const DEFAULT_WEEKS = 8;

const isMovable = (it) =>
  (it.kind === "produksjon" && !["startet", "ferdig"].includes(it.status)) ||
  (it.kind === "montering" && !["montert", "montert_med_restpunkter"].includes(it.status));

const statusText = (it) => {
  if (it.kind === "produksjon") return PRODUCTION_STATUS_LABELS[it.status] || it.status;
  if (it.kind === "montering") return INSTALL_STATUS_LABELS[it.status] || it.status;
  if (it.kind === "service") return SERVICE_STATUS_LABELS[it.status] || it.status;
  if (it.kind === "avtalt_levering") return "Avtalt med kunden";
  return "Kundens ønske – ikke avtalt";
};

const firstName = (n) => (n || "").split(" ")[0];
const reasons = (it) => (it.missing || []).map(readableReason);

/** Second line in a bar: the one thing that matters for this activity. */
const barSub = (it) => {
  if (it.state === "krever_oppfolging") return reasons(it)[0];
  if (it.kind === "montering") return [firstName(it.installer) || "Montør ikke satt", it.date ? formatDate(it.date) : null].filter(Boolean).join(" · ");
  if (it.kind === "produksjon") return it.status === "startet" ? "Startet" : it.status === "ferdig" ? "Ferdig produsert" : it.state === "avventer" ? reasons(it)[0] : "Planlagt";
  if (it.kind === "service") return it.title || firstName(it.installer) || null;
  return null;
};

/* ---------------------------------------------------------------- Activity panel (detail + Endre uke) */

const ActivityPanel = ({ item, onClose }) => {
  const update = useDeliveryUpdater();
  const { data: meta } = useMeta();
  const [editing, setEditing] = useState(false);
  const [week, setWeek] = useState(item.week);
  const [date, setDate] = useState(item.date || "");
  const [installer, setInstaller] = useState(item.installer || "");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const weeks = useMemo(() => weekRange(-2, 24), []);
  const installers = meta ? [meta.employees.ola, meta.employees.eirik] : [];
  const k = KINDS[item.kind];
  const st = STATE[item.state] || STATE.planlagt;
  const movable = isMovable(item);
  const conflict = !!(item.agreed_week && week > item.agreed_week && item.kind !== "avtalt_levering");
  const href = item.kind === "service" ? `/service/${item.service_id}` : `/leveranser/${item.delivery_id}${item.kind === "montering" ? "?tab=montering" : ""}`;
  const { name, unit } = splitProjectName(item);

  useEffect(() => { setEditing(false); setWeek(item.week); setDate(item.date || ""); setInstaller(item.installer || ""); setAck(false); }, [item]);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/deliveries/${item.delivery_id}/plan`, { kind: item.kind, week, date: item.kind === "montering" ? date || null : undefined, installer: item.kind === "montering" ? installer || null : undefined });
      update(null);
      toast.success(`${k.label} planlagt til ${weekLabel(week).toLowerCase()}`, { description: "Manuell planlegging. Avtalt leveringsuke er ikke endret." });
      onClose();
    } catch (e) {
      toast.error(errorMessage(e, "Kunne ikke lagre planen"));
    } finally {
      setBusy(false);
    }
  };

  const rs = reasons(item);

  return (
    <PanelFrame
      testId="plan-activity-dialog"
      eyebrow={`${k.label} · ${weekLabel(item.week)}${item.date ? ` · ${formatDate(item.date)}` : ""}`}
      title={name}
      meta={[unit, item.kind === "service" ? `${item.number} · ${item.delivery_number || ""}`.replace(/ · $/, "") : item.number, item.customer_name].filter(Boolean).join(" · ")}
      onClose={onClose}
      footer={
        <>
          <Link to={href} className="action-link" data-testid="plan-open-delivery">{item.kind === "service" ? "Åpne serviceoppdrag" : "Åpne leveranse"} <ArrowRight /></Link>
          <div className="flex gap-2">
            {!editing && movable && <Button onClick={() => setEditing(true)} data-testid={`plan-move-${item.kind}-${item.number}`}>Endre uke</Button>}
            {editing && <Button variant="ghost" onClick={() => setEditing(false)} data-testid="plan-move-cancel">Avbryt</Button>}
            {editing && <Button onClick={save} disabled={busy || (conflict && !ack)} data-testid="plan-move-save">{busy && <Loader2 className="animate-spin" />} Lagre</Button>}
          </div>
        </>
      }
    >
      {!editing ? (
        <>
          <PanelSection label="Status">
            <p className="text-[15px] font-semibold leading-snug">{statusText(item)}</p>
            {item.state !== "planlagt" ? (
              <div className="mt-2 border-l-2 py-1 pl-3" style={{ borderColor: item.state === "krever_oppfolging" ? "hsl(var(--state-waiting))" : "hsl(var(--border))" }} data-testid="plan-state-note">
                <p className="text-[14.5px] font-medium">{st.label}</p>
                <ul className="mt-0.5 space-y-0.5 text-[14.5px] text-foreground">{rs.map((m) => <li key={m}>{m}</li>)}</ul>
              </div>
            ) : (
              <p className="t-meta mt-1">Ingenting mangler for denne aktiviteten.</p>
            )}
            {item.conflict && <p className="mt-3 text-[14px]" style={{ color: "hsl(var(--state-waiting))" }} data-testid="plan-conflict-note">Planlagt etter avtalt leveringsuke. Avtalt uke endres bare gjennom kundegodkjent endring – avklar med kunden.</p>}
          </PanelSection>

          <PanelSection label="Detaljer">
            <dl className="divide-y border-y">
              {item.kind === "montering" && <div className="grid grid-cols-[112px_1fr] gap-3 py-2"><dt className="t-meta">Montør</dt><dd className="text-[14.5px]">{item.installer || "Ikke satt"}</dd></div>}
              {item.kind === "montering" && item.time_window && <div className="grid grid-cols-[112px_1fr] gap-3 py-2"><dt className="t-meta">Tidsrom</dt><dd className="text-[14.5px]">{item.time_window}</dd></div>}
              {item.kind === "service" && item.installer && <div className="grid grid-cols-[112px_1fr] gap-3 py-2"><dt className="t-meta">Tildelt</dt><dd className="text-[14.5px]">{item.installer}</dd></div>}
              {item.agreed_week && <div className="grid grid-cols-[112px_1fr] gap-3 py-2"><dt className="t-meta">Avtalt levering</dt><dd className="text-[14.5px]">{weekLabel(item.agreed_week)}</dd></div>}
              {item.quantity > 1 && <div className="grid grid-cols-[112px_1fr] gap-3 py-2"><dt className="t-meta">Antall</dt><dd className="text-[14.5px]">{item.quantity} trapper</dd></div>}
              {item.stair_summary && <div className="grid grid-cols-[112px_1fr] gap-3 py-2"><dt className="t-meta">Trapp</dt><dd className="text-[14.5px]">{item.stair_summary}</dd></div>}
              {item.responsible && <div className="grid grid-cols-[112px_1fr] gap-3 py-2"><dt className="t-meta">Ansvarlig</dt><dd className="text-[14.5px]">{item.responsible}</dd></div>}
            </dl>
          </PanelSection>

          {item.kind === "avtalt_levering" && <p className="t-meta mt-6">Avtalt leveringsuke endres ikke i planen, bare gjennom kundegodkjent endring.</p>}
          {item.kind === "onsket_levering" && <p className="t-meta mt-6">Kundens ønske. Leveringsuke avtales når tilbudet aksepteres og grunnlaget er godkjent.</p>}
          {item.kind === "produksjon" && ["startet", "ferdig"].includes(item.status) && <p className="t-meta mt-6">Produksjonen er {item.status === "ferdig" ? "ferdig" : "startet"} – uken kan ikke flyttes her.</p>}
        </>
      ) : (
        <>
          <PanelSection label={`Endre uke · ${k.label.toLowerCase()}`}>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label>Uke</Label>
                <Select value={week} onValueChange={setWeek}>
                  <SelectTrigger data-testid="plan-week-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{weeks.map((w) => <SelectItem key={w} value={w}>{weekLabel(w)} ({w.split("-W")[0]})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {item.kind === "montering" && (
                <>
                  <div className="space-y-1.5"><Label htmlFor="pd">Dato</Label><Input id="pd" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="plan-date-input" /></div>
                  <div className="space-y-1.5">
                    <Label>Montør</Label>
                    <Select value={installer || "none"} onValueChange={(v) => setInstaller(v === "none" ? "" : v)}>
                      <SelectTrigger data-testid="plan-installer-select"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="none">Ikke satt</SelectItem>{installers.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
          </PanelSection>
          <PanelSection label="Konsekvens">
            {conflict ? (
              <div className="border-l-2 py-1 pl-3" style={{ borderColor: "hsl(var(--state-waiting))" }} data-testid="plan-conflict-warning">
                <p className="text-[14.5px] font-medium">{weekLabel(week)} er etter avtalt levering {weekLabel(item.agreed_week).toLowerCase()}</p>
                <p className="t-meta mt-0.5">Avtalt uke endres ikke her. Avviket må avklares med kunden, eventuelt gjennom en endring.</p>
                <label className="mt-2 flex min-h-[40px] cursor-pointer items-center gap-2.5 text-[14px]"><Checkbox checked={ack} onCheckedChange={(v) => setAck(!!v)} data-testid="plan-conflict-ack" /> Avviket er vurdert – planlegg likevel</label>
              </div>
            ) : (
              <ul className="space-y-1 text-[14.5px] text-foreground">
                <li>Avtalt leveringsuke{item.agreed_week ? ` (${weekLabel(item.agreed_week).toLowerCase()})` : ""} flyttes ikke automatisk.</li>
                {item.kind === "produksjon" && <li>Monteringsuken endres ikke – vurder den separat.</li>}
                <li>Manuell plan. Systemet beregner ikke kapasitet eller varighet.</li>
              </ul>
            )}
          </PanelSection>
        </>
      )}
    </PanelFrame>
  );
};

/* ---------------------------------------------------------------- Gantt (desktop) */

const LEFT_W = 272;
const MIN_COL = 116;
const COMPACT_COL = 150;
const BAR_H = 46;
const MS_H = 24;
const GAP = 4;
const ROW_PAD = 8;

const weekRangeText = (monday) => {
  const a = new Date(monday);
  const b = new Date(a.getTime() + 6 * 86400000);
  const sameMonth = a.getMonth() === b.getMonth();
  const fmt = (d, withMonth) => d.toLocaleDateString("nb-NO", withMonth ? { day: "numeric", month: "short" } : { day: "numeric" });
  return `${fmt(a, !sameMonth)}–${fmt(b, true)}`;
};

const Bar = ({ it, left, top, width, onOpen, active }) => {
  const k = KINDS[it.kind];
  const sub = barSub(it);
  const follow = it.state === "krever_oppfolging";
  const all = reasons(it);
  return (
    <button
      type="button"
      className="gantt-bar"
      style={{ "--kind": k.color, left, top, width, height: BAR_H }}
      data-kind={it.kind}
      data-state={it.state}
      data-status={it.status}
      aria-pressed={active}
      onClick={() => onOpen(it)}
      data-testid={`plan-item-${it.kind}-${it.number}`}
      aria-label={`${k.label} ${it.project_name} ${weekLabel(it.week)}${sub ? ` – ${sub}` : ""}`}
      title={`${k.label} · ${it.project_name} · ${weekLabel(it.week)}${all.length ? ` · ${all.join(" · ")}` : sub ? ` · ${sub}` : ""}`}
    >
      <span className="b1">{k.label}{it.kind === "service" ? ` ${it.number}` : ""}</span>
      {sub && <span className="b2">{follow && <AlertCircle aria-hidden="true" />}{sub}{all.length > 1 ? ` +${all.length - 1}` : ""}</span>}
    </button>
  );
};

const Milestone = ({ it, left, top, width, onOpen, active, compact }) => {
  const k = KINDS[it.kind];
  const desired = it.kind === "onsket_levering";
  const text = compact ? (desired ? "Ønsket" : "Avtalt") : k.label;
  return (
    <button
      type="button"
      className="gantt-ms"
      style={{ "--kind": k.color, left, top, width, height: MS_H }}
      data-kind={it.kind}
      aria-pressed={active}
      onClick={() => onOpen(it)}
      data-testid={`plan-item-${it.kind}-${it.number}`}
      aria-label={`${k.label} ${it.project_name} ${weekLabel(it.week)}`}
      title={`${k.label} · ${it.project_name} · ${weekLabel(it.week)}${desired ? " · ikke avtalt" : ""}`}
    >
      <span className="mark" aria-hidden="true" />
      <span className="ms-text">{text}</span>
      {desired && !compact && <span className="ms-sub">· ikke avtalt</span>}
    </button>
  );
};

const Gantt = ({ weeks, rows, onOpen, activeKey, activeRow }) => {
  const ref = useRef(null);
  const width = useElementWidth(ref);
  const colW = Math.max(MIN_COL, Math.floor((Math.max(0, width - LEFT_W - 2)) / weeks.length));
  const idxOf = Object.fromEntries(weeks.map((w, i) => [w.week, i]));
  const curIdx = weeks.findIndex((w) => w.is_current);
  const trackW = weeks.length * colW;
  let todayX = null;
  if (curIdx >= 0) {
    const monday = new Date(weeks[curIdx].monday);
    const frac = Math.min(1, Math.max(0, (Date.now() - monday.getTime()) / (7 * 86400000)));
    todayX = curIdx * colW + frac * colW;
  }
  const inner = colW - 12;
  const compact = colW < COMPACT_COL;

  return (
    <div className="gantt" ref={ref} data-testid="plan-grid" style={{ "--col-w": `${colW}px`, "--left-w": `${LEFT_W}px` }}>
      <div className="gantt-inner" style={{ width: LEFT_W + trackW }}>
        <div className="gantt-head">
          <div className="gantt-corner"><p className="t-label">Leveranse</p></div>
          <div className="flex" style={{ width: trackW }}>
            {weeks.map((w) => (
              <div key={w.week} className={`gantt-week ${w.is_current ? "is-current" : ""}`} style={{ width: colW }} data-testid={`plan-week-${w.week}`}>
                <p className="wk">{w.label}{w.is_current ? <span className="ml-1.5 text-[12px] font-medium">nå</span> : ""}</p>
                <p className="rng">{weekRangeText(w.monday)}</p>
              </div>
            ))}
          </div>
        </div>

        {rows.map((r) => {
          let maxH = 0;
          const layout = {};
          for (const w of Object.keys(r.byWeek)) {
            const bars = r.byWeek[w].filter((it) => KINDS[it.kind].type === "bar");
            const ms = r.byWeek[w].filter((it) => KINDS[it.kind].type === "milestone");
            let y = ROW_PAD;
            const placed = [];
            for (const it of bars) { placed.push({ it, top: y, h: BAR_H }); y += BAR_H + GAP; }
            for (const it of ms) { placed.push({ it, top: y, h: MS_H }); y += MS_H + GAP; }
            layout[w] = placed;
            maxH = Math.max(maxH, y - GAP + ROW_PAD);
          }
          const h = Math.max(maxH, ROW_PAD * 2 + BAR_H);
          const { name, unit } = splitProjectName(r);
          return (
            <div key={r.key} className="gantt-row" style={{ height: h }} aria-selected={activeRow === r.key} data-testid={`plan-row-${r.number}`}>
              <div className="gantt-label">
                <Link to={r.href} className="block min-w-0 transition-colors hover:text-primary" title={`${r.project_name} · ${r.number} · ${r.customer_name}`}>
                  <p className="row-name truncate">{name}</p>
                  <p className="t-meta truncate">{[unit, r.number, r.customer_name].filter(Boolean).join(" · ")}</p>
                </Link>
              </div>
              <div className="gantt-track" style={{ width: trackW }}>
                {curIdx >= 0 && <div className="gantt-current" style={{ left: curIdx * colW, width: colW }} aria-hidden="true" />}
                {Object.entries(layout).map(([w, placed]) => placed.map(({ it, top }) => {
                  const key = `${it.kind}-${it.number}`;
                  const common = { it, left: idxOf[w] * colW + 6, top, width: inner, onOpen, active: activeKey === key, compact };
                  return KINDS[it.kind].type === "bar" ? <Bar key={key} {...common} /> : <Milestone key={key} {...common} />;
                }))}
                {todayX !== null && <div className="gantt-today" style={{ left: todayX }} aria-hidden="true" />}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ---------------------------------------------------------------- Week agenda (mobile) */

const Agenda = ({ weeks, items, onOpen }) => {
  const curIdx = Math.max(0, weeks.findIndex((w) => w.is_current));
  const [idx, setIdx] = useState(curIdx);
  const w = weeks[Math.min(idx, weeks.length - 1)];
  const list = items.filter((i) => i.week === w.week);
  const groups = [
    ["Produksjon", list.filter((i) => i.kind === "produksjon")],
    ["Montering", list.filter((i) => i.kind === "montering")],
    ["Service", list.filter((i) => i.kind === "service")],
    ["Leveringer", list.filter((i) => ["avtalt_levering", "onsket_levering"].includes(i.kind))],
  ];
  return (
    <div data-testid="plan-agenda">
      <div className="flex items-center justify-between border-b pb-2">
        <Button variant="ghost" size="icon" className="h-11 w-11" onClick={() => setIdx(Math.max(0, idx - 1))} disabled={idx === 0} aria-label="Forrige uke"><ChevronLeft /></Button>
        <div className="text-center"><p className="text-[16px] font-semibold">{w.label}{w.is_current ? " · nå" : ""}</p><p className="t-meta">{weekRangeText(w.monday)}</p></div>
        <Button variant="ghost" size="icon" className="h-11 w-11" onClick={() => setIdx(Math.min(weeks.length - 1, idx + 1))} disabled={idx === weeks.length - 1} aria-label="Neste uke"><ChevronRight /></Button>
      </div>
      {list.length === 0 && <p className="t-body py-8 text-center text-muted-foreground">Ingenting planlagt denne uken.</p>}
      {groups.filter(([, g]) => g.length).map(([title, g]) => (
        <section key={title} className="mt-5">
          <h2 className="t-label mb-1">{title}</h2>
          <ul className="divide-y border-y">
            {g.map((it) => {
              const k = KINDS[it.kind];
              const sub = it.kind === "avtalt_levering" ? "Avtalt med kunden" : it.kind === "onsket_levering" ? "Ikke avtalt" : barSub(it);
              return (
                <li key={`${it.kind}-${it.number}`}>
                  <button type="button" className="flex min-h-[60px] w-full items-center gap-3 py-2.5 text-left" onClick={() => onOpen(it)} data-testid={`agenda-item-${it.kind}-${it.number}`}>
                    {k.type === "bar" ? <span className="lg-bar shrink-0" style={{ "--kind": k.color }} aria-hidden="true" /> : <span className={`lg-ms shrink-0 ${it.kind === "onsket_levering" ? "is-hollow" : ""}`} style={{ "--kind": k.color }} aria-hidden="true" />}
                    <div className="min-w-0 flex-1">
                      <p className="text-[15px] font-medium leading-snug">{it.project_name}</p>
                      <p className={`t-meta truncate ${it.state === "krever_oppfolging" ? "!text-[hsl(var(--state-waiting))] font-medium" : ""}`}>{k.label}{it.kind === "service" ? ` ${it.number}` : ""}{sub ? ` · ${sub}` : ""}</p>
                    </div>
                    {it.state === "krever_oppfolging" && <AlertCircle className="h-4 w-4 shrink-0" style={{ color: "hsl(var(--state-waiting))" }} aria-label="Krever oppfølging" />}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}
    </div>
  );
};

/* ---------------------------------------------------------------- Page */

export default function PlanPage() {
  const [params, setParams] = useSearchParams();
  const offset = Number.isFinite(Number(params.get("fra"))) && params.get("fra") !== null ? Number(params.get("fra")) : -1;
  const weeksN = WEEK_OPTIONS.includes(Number(params.get("uker"))) ? Number(params.get("uker")) : DEFAULT_WEEKS;
  const kind = params.get("type") || "alle";
  const onlyFollowUp = params.get("vis") === "oppfolging";
  const activeKey = params.get("valgt");
  const { data, isLoading, isError, refetch } = usePlan(offset, weeksN);

  const setParam = useCallback((k, v, def) => setParams((p) => {
    const n = new URLSearchParams(p);
    if (v === null || v === undefined || String(v) === String(def)) n.delete(k); else n.set(k, String(v));
    return n;
  }, { replace: true }), [setParams]);
  const closePanel = useCallback(() => setParam("valgt", null), [setParam]);
  const open = (it) => setParam("valgt", `${it.kind}-${it.number}`);

  const items = useMemo(() => {
    if (!data) return [];
    return data.items
      .filter((i) => kind === "alle" || (kind === "levering" ? ["avtalt_levering", "onsket_levering"].includes(i.kind) : i.kind === kind))
      .filter((i) => !onlyFollowUp || i.state === "krever_oppfolging");
  }, [data, kind, onlyFollowUp]);

  const { rows, hidden } = useMemo(() => {
    if (!data) return { rows: [], hidden: 0 };
    const visible = new Set(data.weeks.map((w) => w.week));
    const map = new Map();
    let outside = 0;
    for (const it of items) {
      if (!visible.has(it.week)) { outside += 1; continue; }
      const key = it.delivery_id;
      if (!map.has(key)) map.set(key, { key, number: it.kind === "service" ? it.delivery_number || it.number : it.number, project_name: it.project_name, project_group: it.project_group, customer_name: it.customer_name, href: `/leveranser/${it.delivery_id}`, byWeek: {}, first: it.week });
      const r = map.get(key);
      (r.byWeek[it.week] ||= []).push(it);
      if (it.week < r.first) r.first = it.week;
    }
    for (const r of map.values()) for (const w of Object.keys(r.byWeek)) r.byWeek[w].sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind));
    return { rows: Array.from(map.values()).sort((a, b) => a.first.localeCompare(b.first) || a.project_name.localeCompare(b.project_name)), hidden: outside };
  }, [data, items]);

  const active = useMemo(() => (data && activeKey ? data.items.find((i) => `${i.kind}-${i.number}` === activeKey) || null : null), [data, activeKey]);

  if (isLoading) return <div className="page"><LoadingBlock rows={6} /></div>;
  if (isError) return <div className="page"><ErrorBlock onRetry={refetch} /></div>;

  const followUps = data.items.filter((i) => i.state === "krever_oppfolging").length;
  const first = data.weeks[0];
  const last = data.weeks[data.weeks.length - 1];
  const step = Math.max(1, Math.floor(weeksN / 2));

  const about = (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" className="link-quiet inline-flex items-center gap-1 text-[13px]" data-testid="plan-about"><Info className="h-3.5 w-3.5" /> Om planen</button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[360px] space-y-2 text-[14px] leading-relaxed" data-testid="plan-about-content">
        <p className="font-semibold">Manuell ukeplan</p>
        <p>Planen viser hvilken uke produksjon, montering og service er lagt til, og hvilken uke levering er avtalt med kunden. Ukene settes manuelt – systemet beregner ikke kapasitet eller varighet.</p>
        <p>Felt er aktiviteter. Rombemarkører er leveringsmilepæler: fylt for avtalt levering, åpen for kundens ønske som ikke er avtalt.</p>
        <p>Velg en aktivitet for detaljer og «Endre uke». Avtalt leveringsuke endres aldri her – bare gjennom kundegodkjent endring.</p>
      </PopoverContent>
    </Popover>
  );

  return (
    <SplitPage panel={active ? <ActivityPanel item={active} onClose={closePanel} /> : null} onClose={closePanel} title="Aktivitet">
      <div className="page" data-testid="plan-page">
        <PageHeader title="Plan" meta={<span className="inline-flex flex-wrap items-center gap-x-2">{`${first.label} – ${last.label.toLowerCase()} · Manuell ukeplan`} <span aria-hidden="true">·</span> {about}</span>} />

        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="plan-toolbar">
          <div className="seg" role="group" aria-label="Periode">
            <button type="button" onClick={() => setParam("fra", offset - step, -1)} aria-label="Tidligere uker" data-testid="plan-prev"><ChevronLeft className="h-4 w-4" /></button>
            <button type="button" onClick={() => setParam("fra", -1, -1)} data-testid="plan-today" aria-pressed={offset === -1}>Denne uken</button>
            <button type="button" onClick={() => setParam("fra", offset + step, -1)} aria-label="Senere uker" data-testid="plan-next"><ChevronRight className="h-4 w-4" /></button>
          </div>
          <div className="seg hidden lg:inline-flex" role="group" aria-label="Antall uker" data-testid="plan-weeks">
            {WEEK_OPTIONS.map((n) => <button key={n} type="button" aria-pressed={weeksN === n} onClick={() => setParam("uker", n, DEFAULT_WEEKS)} data-testid={`plan-weeks-${n}`}>{n} uker</button>)}
          </div>
          <Select value={kind} onValueChange={(v) => setParam("type", v, "alle")}>
            <SelectTrigger className="ctl h-9 w-auto gap-2 border-0 shadow-none focus:ring-1" data-testid="plan-filter" aria-label="Aktivitet"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="alle">Alle aktiviteter</SelectItem>
              <SelectItem value="produksjon">Produksjon</SelectItem>
              <SelectItem value="montering">Montering</SelectItem>
              <SelectItem value="service">Service</SelectItem>
              <SelectItem value="levering">Leveringer</SelectItem>
            </SelectContent>
          </Select>
          <div className="seg" role="group" aria-label="Vis">
            <button type="button" aria-pressed={!onlyFollowUp} onClick={() => setParam("vis", null)} data-testid="plan-show-all">Alle</button>
            <button type="button" aria-pressed={onlyFollowUp} onClick={() => setParam("vis", "oppfolging")} data-testid="plan-show-followup"><AlertCircle className="h-3.5 w-3.5" style={{ color: followUps ? "hsl(var(--state-waiting))" : undefined }} /> Krever oppfølging {followUps ? <span className="seg-count">{followUps}</span> : null}</button>
          </div>
          <div className="ml-auto hidden flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px] text-muted-foreground lg:flex" aria-hidden="true" data-testid="plan-legend">
            {["produksjon", "montering", "service"].map((k) => <span key={k} className="inline-flex items-center gap-1.5"><span className="lg-bar" style={{ "--kind": KINDS[k].color }} /> {KINDS[k].label}</span>)}
            <span className="inline-flex items-center gap-1.5"><span className="lg-ms" style={{ "--kind": KINDS.avtalt_levering.color }} /> Avtalt levering</span>
            <span className="inline-flex items-center gap-1.5"><span className="lg-ms is-hollow" /> Ønsket levering</span>
          </div>
        </div>

        <div className="hidden lg:block">
          {rows.length === 0 ? (
            <div className="frame flex min-h-[320px] flex-col items-center justify-center gap-2 text-center" data-testid="plan-empty">
              <p className="text-[15px] font-medium">Ingen aktiviteter i disse ukene{onlyFollowUp ? " som krever oppfølging" : ""}.</p>
              <p className="t-meta">Bla i perioden eller vis flere uker.</p>
            </div>
          ) : (
            <Gantt weeks={data.weeks} rows={rows} onOpen={open} activeKey={activeKey} activeRow={active?.delivery_id} />
          )}
          <p className="t-meta mt-2 flex flex-wrap items-center gap-x-3" data-testid="plan-footnote">
            <span>{rows.length} leveranser med aktivitet i perioden{hidden ? ` · ${hidden} aktiviteter utenfor visningen` : ""}</span>
            <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> Uker settes manuelt · ingen kapasitetsberegning</span>
          </p>
        </div>

        <div className="lg:hidden">
          <Agenda weeks={data.weeks} items={items} onOpen={open} />
        </div>
      </div>
    </SplitPage>
  );
}
